// Kartons und Stueck je Karton einer Container-Position direkt aendern (22.10.2026 Schnitt 4).
// Menge, EK und Packstation-Anteil bleiben unberuehrt; der Server sperrt nach Zustellung, WE
// oder Archiv wie beim Packstation-Anteil. Passt Kartons × Stueck nicht zur Menge, bleibt die
// Klaerung im Arbeitsplatz offen — gespeichert wird trotzdem, so wie die Packliste es sagt.
import { useRef, useState } from "react";
import { toast } from "sonner";
import { useSetShipmentItemPackdaten, type PurchaseShipmentItem } from "@/api/purchase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fehlerText } from "@/lib/fehlerText";

const MAX_INT4 = 2147483647;

const ganz = (v: string): number | null => (/^\d+$/.test(v.trim()) ? Number(v.trim()) : null);
const zahl = (n: number) => n.toLocaleString("de-DE");

export function PackdatenEingabe({ item, stern, meter = false, onFertig }: {
  item: PurchaseShipmentItem;
  stern: string;
  /** Meterware: Menge und Kapazitaet in m statt St. */
  meter?: boolean;
  onFertig?: () => void;
}) {
  const mutation = useSetShipmentItemPackdaten(item.id);
  const [kartons, setKartons] = useState(item.cartons == null ? "" : String(item.cartons));
  const [stueck, setStueck] = useState(item.pieces_per_carton == null ? "" : String(item.pieces_per_carton));
  const [fehler, setFehler] = useState<string | null>(null);
  const riegel = useRef(false);
  const d = item.packstation_kartons ?? 0;
  const e = meter ? "m" : "St.";
  const k = ganz(kartons);
  const s = ganz(stueck);
  const kapazitaet = k !== null && s !== null && s > 0 ? k * s : null;
  // Wie die Pruefung des Servers (validateShipmentPacking): der letzte Karton darf angebrochen, nicht leer sein.
  const passt = kapazitaet !== null && kapazitaet >= item.qty && kapazitaet - item.qty < s!;

  const speichern = () => {
    if (riegel.current) return;
    setFehler(null);
    if (k === null || k > MAX_INT4) return setFehler("Kartons: ganze Zahl ab 0");
    if (s === null || s < 1 || s > MAX_INT4) return setFehler("Stück je Karton: ganze Zahl ab 1");
    if (k * s > MAX_INT4) return setFehler("Kartons × Stück je Karton ist zu groß");
    if (k < d) return setFehler(`Mindestens ${zahl(d)} Kartons: so viele gehen direkt in die Packstation`);
    if (k === item.cartons && s === item.pieces_per_carton) return;
    riegel.current = true;
    mutation.mutate({ cartons: k, pieces_per_carton: s }, {
      onSuccess: () => { toast.success(`Packdaten gespeichert: ${stern}`); onFertig?.(); },
      onError: (err) => setFehler(fehlerText(err, "Packdaten konnten nicht gespeichert werden")),
      onSettled: () => { riegel.current = false; },
    });
  };

  return (
    <div className="grid gap-1.5 text-xs">
      <div className="flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11px] text-ink-2">
          Kartons
          <Input value={kartons} inputMode="numeric" aria-label={`Kartons für ${stern}`} className="h-8 w-24 font-mono text-sm"
            onChange={(ev) => setKartons(ev.target.value)} />
        </label>
        <label className="grid gap-1 text-[11px] text-ink-2">
          Stück je Karton
          <Input value={stueck} inputMode="numeric" aria-label={`Stück je Karton für ${stern}`} className="h-8 w-24 font-mono text-sm"
            onChange={(ev) => setStueck(ev.target.value)} />
        </label>
        <Button type="button" size="sm" disabled={mutation.isPending} onClick={speichern}
          aria-label={`Packdaten speichern für ${stern}`}>
          {mutation.isPending ? "Speichere…" : "Speichern"}
        </Button>
      </div>
      {kapazitaet !== null ? (
        <p className="text-[11px] text-ink-2">
          <span className="font-mono">{`${zahl(k!)} × ${zahl(s!)} = ${zahl(kapazitaet)} ${e} · Menge ${zahl(item.qty)} ${e}`}</span>
          {passt ? null : (
            <span className="ml-2 text-warn">{`Kartons × Stück passt nicht zur Menge ${zahl(item.qty)} ${e}: die Klärung bleibt offen.`}</span>
          )}
        </p>
      ) : null}
      {fehler ? <p role="alert" className="text-coral">{fehler}</p> : null}
      <p className="text-[11px] leading-snug text-ink-2">
        Ändert nur Kartons und Stück je Karton. Menge, EK und Packstation-Anteil bleiben.
      </p>
    </div>
  );
}
